import React, { useState, useEffect } from 'react';
import { Megaphone, X, PenLine } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const getAuthToken = () => typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;

interface Suggestion {
  id: string;
  suggestion_type: string;
  target_type: string | null;
  target_id: string | null;
  suggestion_text: string;
}

interface PostSuggestionBannerProps {
  onCreatePost?: (text: string) => void;
}

export default function PostSuggestionBanner({ onCreatePost }: PostSuggestionBannerProps) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;
    fetch(`${API_URL}/engagement/suggestions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.suggestions?.length > 0) setSuggestions(d.suggestions); })
      .catch(() => {});
  }, []);

  const dismiss = async (id: string) => {
    setSuggestions(prev => prev.filter(s => s.id !== id));
    const token = getAuthToken();
    if (!token) return;
    fetch(`${API_URL}/engagement/suggestions/${id}/dismiss`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  };

  if (suggestions.length === 0) return null;
  const s = suggestions[0];

  return (
    <div className="mb-4 rounded-xl bg-emerald-500/6 border border-emerald-500/15 p-3.5">
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/20 shrink-0 mt-0.5">
          <Megaphone size={14} className="text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white leading-relaxed">{s.suggestion_text}</p>
          <div className="flex items-center gap-2 mt-2.5">
            <button
              onClick={() => onCreatePost?.(s.suggestion_text.split('"')[1] || '')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/20 text-xs font-medium text-emerald-300 hover:bg-emerald-500/30 transition-colors"
            >
              <PenLine size={12} />
              Create Post
            </button>
            <button
              onClick={() => dismiss(s.id)}
              className="flex items-center gap-1 text-[11px] text-[#9a9aac] hover:text-white transition-colors px-2 py-1"
            >
              <X size={12} />
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
